import { useConvexAuth, useQuery } from "convex/react";
import { Navigate } from "react-router-dom";
import { useAuthActions } from "@convex-dev/auth/react";
import { ShieldX } from "lucide-react";
import { api } from "../../convex/_generated/api";

type Role = "admin" | "moderator" | "partner" | "user";

export function ProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles?: Role[];
}) {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const profile = useQuery(
    api.users.currentProfile,
    isAuthenticated ? {} : "skip"
  );

  const { signOut } = useAuthActions();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F5F6F8]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#1A224C] border-t-transparent rounded-full animate-spin" />
          <span className="font-jetbrains text-[12px] tracking-[1px] text-[#8B92A0]">
            LOADING...
          </span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Still loading profile
  if (profile === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F5F6F8]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#1A224C] border-t-transparent rounded-full animate-spin" />
          <span className="font-jetbrains text-[12px] tracking-[1px] text-[#8B92A0]">
            VERIFYING ACCESS...
          </span>
        </div>
      </div>
    );
  }

  // Check if account is suspended — show inline, don't redirect (avoids loop)
  if (profile && profile.isSuspended) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F5F6F8]">
        <div className="flex flex-col items-center gap-6 max-w-[400px] text-center p-8">
          <div className="flex items-center justify-center w-16 h-16 bg-[#EF4444]/10">
            <ShieldX className="w-8 h-8 text-[#EF4444]" />
          </div>
          <div className="flex flex-col gap-2">
            <h1 className="font-space text-[28px] font-bold tracking-[-1px] text-[#1A224C]">
              ACCOUNT SUSPENDED
            </h1>
            <p className="font-jetbrains text-[13px] leading-[1.6] text-[#6B7280]">
              Your account has been suspended. Please contact your administrator for more information.
            </p>
          </div>
          <button
            onClick={() => signOut()}
            className="flex items-center justify-center w-full bg-[#1A224C] py-3.5 px-8 font-jetbrains text-[12px] font-bold tracking-[1.5px] text-white hover:opacity-90 transition-opacity cursor-pointer border-none"
          >
            SIGN OUT
          </button>
        </div>
      </div>
    );
  }

  // Check role if specified
  if (allowedRoles && profile && !allowedRoles.includes(profile.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
